<template>
    <div>
        <div class="Step">
            <Transition name="fade" mode="out-in">
                <q-inner-loading :showing="isLoading" />
            </Transition>
            <Preview
                v-if="preview && !isLoading"
                v-bind="preview"
                :modelValue="modelValue.config"
                :loading="isLoading"
                :initialNumberFormat="numberFormat"
                :numberFormats="numberFormats"
                @update:modelValue="handleInput"
                @update:formatNumber="handleFormatNumber"
            />
        </div>
    </div>
</template>

<script>
import VueTypes from 'vue-types'
import { Api, apiRequest, notifyError } from '@/api'
import Config from '@/config'
import Preview from './Preview/Preview.vue'

const PREVIEW_LENGTH = 10

const normalizeCols = (cols = []) => cols.map((col) => ({
    align: 'left',
    field: col.name,
    label: col.name,
    name: col.name,
    type: col.type,
    selectableAsActivity: col.selectableAsActivity,
}))

const normalizeRows = (rows = []) => rows.map((data, index) => ({ ...data.row, index }))

export default {
    name: 'StepFields',
    components: {
        Preview,
    },
    inject: ['App'],
    props: {
        datasetId: VueTypes.string,
        projectId: VueTypes.string,
        modelValue: VueTypes.shape({
            config: VueTypes.any.isRequired,
        }).loose,
    },
    emits: ['update:modelValue'],
    data() {
        const numberFormats = this.getNumberFormats()
        return {
            preview: undefined,
            isLoading: true,
            numberFormat: numberFormats.find(({ value }) => value === (this.modelValue?.config?.numberFormat || 'en'))
                || numberFormats.find(({ value }) => value === 'en'),
            numberFormats,
        }
    },
    watch: {
        datasetId: {
            handler(value) {
                if (!value) return
                this.getPreview()
            },
            immediate: true,
        },
        'modelValue.config.numberFormat': {
            handler(value) {
                if (!value) return
                if (this.numberFormat?.value === value) return
                this.numberFormat = this.numberFormats.find(({ value: v }) => v === value) || this.numberFormat
                if (this.datasetId) this.getPreview()
            },
        },
    },
    mounted() {
        if (this.datasetId) this.getPreview()
    },
    methods: {
        isValid() {
            const { traceId, activity, start } = this.modelValue?.config || {}
            return !!(traceId && activity && start)
        },
        handleInput(config) {
            this.$emit('update:modelValue', {
                ...this.modelValue,
                config,
            })
        },
        handleFormatNumber(numberFormat) {
            this.numberFormat = this.numberFormats.find(({ value }) => value === numberFormat) || this.numberFormat
            this.$emit('update:modelValue', {
                ...this.modelValue,
                config: {
                    ...this.modelValue.config,
                    numberFormat,
                },
            })
            if (this.datasetId) this.getPreview()
        },
        getNumberFormats() {
            const { locationFormats } = Config.i18n
            return locationFormats.map((val) => ({
                label: this.$t(`wizard.preview.numbersFormats.${val}`),
                value: val,
                description: `${this.$n(123456.05, 'decimal', val === 'eu' ? 'de-DE' : val)}`,
            }))
        },
        async getPreview() {
            const { decimalCharacter, thousandCharacter } = this.App.getNumberFormatCharacters(this.numberFormat.value)
            const params = {
                length: PREVIEW_LENGTH,
                groupingSeparator: thousandCharacter,
                decimalSeparator: decimalCharacter,
            }
            this.isLoading = true
            const { datasetId } = this
            if (!datasetId) {
                this.isLoading = false
                return
            }
            if (datasetId) {
                apiRequest(Api().datasets.preview({ datasetId, params }))
                    .then((preview) => {
                        const { columns, rows } = preview || {}
                        this.preview = {
                            columns: normalizeCols(columns),
                            rawColumns: columns,
                            rows: normalizeRows(rows),
                        }
                        this.$emit('update:modelValue', {
                            ...this.modelValue,
                            config: {
                                ...this.modelValue.config,
                                numberFormat: this.numberFormat?.value,
                                decimalCharacter,
                                thousandCharacter,
                            },
                            preview,
                        })
                    })
                    .catch(notifyError)
                    .finally(() => { this.isLoading = false })
            }
        },
    },
}
</script>
